import React, { useCallback, useEffect, useState } from 'react';

import { usePathname, useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import { useStoreContext } from 'features/store';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { useStorePosConnectivityStatus } from 'features/store/hooks/use-store-connectivity-status';
import { Spinner } from 'native-base';

import { useMaybePlayAudioOnceOnMount } from 'components/app-takeover/play-audio-once';
import { useAppTakeover } from 'components/app-takeover/use-app-takeover';
import { CardModal } from 'components/card-modal';
import { RemoveUnavailableItemsModal } from 'components/remove-unavailable-items-modal';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import CartContentControl from 'experiments/checkout-v2/control/pages/cart/cart-content';
import CartContentTreatment, {
  ICartContent,
} from 'experiments/checkout-v2/treatment/pages/cart/cart-content';
import { useEnableCheckoutV2Experiment } from 'experiments/checkout-v2/use-enable-checkout-v2-experiment';
import useAuthRedirects from 'hooks/auth/use-auth-redirects';
import { useCart } from 'hooks/use-cart';
import useDebounceEffect from 'hooks/use-debounce-effect';
import useEffectOnce from 'hooks/use-effect-once';
import useFeatureMenuAddOn from 'hooks/use-feature-menu-add-on';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { usePrevious } from 'hooks/use-previous';
import { logCheckoutEvent } from 'state/amplitude';
import { useCartContext } from 'state/cart';
import { useIsLegacyCartReady } from 'state/cart/hooks/use-is-legacy-cart-ready';
import { selectors, useAppSelector } from 'state/global-state';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import { cartHasItemsOrCombos, shouldRedirectToMenu } from '../../utils/cart-utils';

import CartError from './cart-error';

const CartContent = (props: ICartContent) => {
  const isCheckoutV2 = useEnableCheckoutV2Experiment();

  useMaybePlayAudioOnceOnMount(useAppTakeover()?.cart?.audio, {
    priority: 1,
    globalPlayOnce: true,
  });

  return isCheckoutV2 ? <CartContentTreatment {...props} /> : <CartContentControl {...props} />;
};

export const Cart = () => {
  const { cartEntries } = useCart();
  const isLegacyCartReady = useIsLegacyCartReady();
  const {
    serverOrder,
    // TODO: BKPE-1956 - unavailableCartEntries
    unavailableCartEntries,
    orderStatus,
    cardSuccessfullyAdded,
    tryPricing,
    shouldSkipPriceOrder,
    setShouldSkipPriceOrder,
    isOrderPriceLoading,
  } = useCartContext();
  const { serviceMode, isDelivery } = useServiceModeContext();

  const { shouldConfirmStoreOnCheckout } = useUIContext();

  const { sections: addOnSections } = useFeatureMenuAddOn();
  const { checkoutPaymentMethod } = usePaymentContext();
  const isSignUpAfterCartEnabled = useIsSignUpAfterCartEnabled();
  const { isStoreOpenAndAvailable, store } = useStoreContext();
  const { getStoreMenu } = useMainMenuContext();
  const { region } = useLocale();
  const pathname = usePathname();
  const appliedLoyaltyRewardsArray = useAppSelector(
    selectors.loyalty.selectAppliedLoyaltyRewardsArray
  );
  const router = useRouter();
  const { isStoreOrderUnavailableDialogOpen } = useUIContext();
  const storeId = store.number || '';

  const { isRestaurantPosOnline } = useStorePosConnectivityStatus({
    storeId,
  });

  const { isStoreAvailableForPickup, isStoreAvailableForDelivery } = useStoreAvailabilityFns();

  const { isStoreChangeHydrationInProgress } = useOrderContext();

  // DO NOT USE service mode on order context. Use service mode on server order
  const orderServiceMode = serverOrder?.cart?.serviceMode;
  const isLoadingOffers = useAppSelector(selectors.loyalty.selectOffersLoading);
  const isLoading = isOrderPriceLoading || isLoadingOffers;
  const unavailableCartItems = unavailableCartEntries.length > 0;
  const [showUnavailableItemsModal, setShowUnavailableItemsModal] = useState(false);

  // check if cart entries don't have item or combos
  const hasItemsOrCombos = cartHasItemsOrCombos(cartEntries);
  // to allow below dismissAndReplace to run with expo router which can't navigate on
  // first render if cart deep link was used
  const [isMounted, setIsMounted] = useState(false);

  useAuthRedirects({ skip: isSignUpAfterCartEnabled });

  const prevCartEntriesLength = usePrevious(cartEntries.length);

  useEffectOnce(() => {
    setIsMounted(true);
  });

  useEffect(() => {
    if (isStoreOpenAndAvailable && serviceMode && cartEntries.length > 0) {
      logCheckoutEvent({
        serviceMode,
        cartEntries,
        shouldLogIndividualProducts: !prevCartEntriesLength,
      });
    }
  }, [cartEntries, isStoreOpenAndAvailable, serviceMode, prevCartEntriesLength]);

  useDebounceEffect(
    () => {
      if (
        !isStoreOpenAndAvailable ||
        !serviceMode ||
        !hasItemsOrCombos ||
        isStoreChangeHydrationInProgress
      ) {
        return;
      }

      shouldSkipPriceOrder && setShouldSkipPriceOrder(false);

      tryPricing();
    },
    250,
    // Changes on rewards should trigger a reprice. That side effect is not captured within the other deps.
    // This usually triggers a second PriceOrder, because the first attempt usually have null rewards
    // until the Redux slice sync-up with the LocalStorage (only happens on page refresh web).
    [appliedLoyaltyRewardsArray, isStoreOpenAndAvailable, serviceMode, tryPricing]
  );

  useEffect(() => {
    if (storeId) {
      getStoreMenu({ region, storeId });
    }
  }, [getStoreMenu, region, storeId]);

  useEffect(() => {
    setShowUnavailableItemsModal(
      !isLoading && unavailableCartItems && !isStoreOrderUnavailableDialogOpen
    );
  }, [isLoading, unavailableCartItems, isStoreOrderUnavailableDialogOpen]);

  const handleUnavailableModalDismiss = useCallback(() => {
    setShowUnavailableItemsModal(false);
    tryPricing();
  }, [tryPricing]);

  useEffect(() => {
    // Since multiple routes are under the same (cart) route group,
    // the cart component doesn't unmount when navigating to other routes like (/checkout), causing
    // isMounted to remain true. This ensures isMounted is only true when actually
    // on the cart route, preventing premature navigation logic execution.
    const isCartRoute = pathname === routes.cart;
    setIsMounted(isCartRoute);
  }, [pathname]);

  useEffect(() => {
    if (
      shouldRedirectToMenu({
        isMounted,
        cartEntries,
        orderStatus,
        serviceMode,
        orderServiceMode,
        hasItemsOrCombos,
      })
    ) {
      // Add delay to ensure iOS native modal view cache is cleared before navigation
      // This prevents cached ActionSheet snapshots from appearing during the transition
      requestAnimationFrame(() => {
        setTimeout(() => {
          router.replace(routes.menu);
        });
      });
    }
  }, [
    router,
    isMounted,
    cartEntries,
    orderStatus,
    serviceMode,
    orderServiceMode,
    hasItemsOrCombos,
  ]);

  // TODO: We should either use these or isStoreOpenAndAvailable from the store context
  const storeAvailableForPickup = isStoreAvailableForPickup(store);
  const storeAvailableForDelivery = isStoreAvailableForDelivery(store);
  const isStoreAvailable = isDelivery ? storeAvailableForDelivery : storeAvailableForPickup;

  const handleDismissCartErrorModal = () => {
    router.replace(routes.menu);
    shouldConfirmStoreOnCheckout(true);
  };

  if (!isLegacyCartReady) {
    return (
      <CardModal
        key="cart"
        dismissType="back"
        invertHeaderColor
        headerText={t('yourOrder')}
        children={<Spinner marginTop={'50%'} />}
        webAnimate={false}
        isFullScreen
      />
    );
  }

  if (!isStoreAvailable) {
    return (
      <CartError
        onDismiss={handleDismissCartErrorModal}
        additionalLoggingContext={{
          isRestaurantPosOnline,
          shouldSkipPriceOrder,
        }}
      />
    );
  }

  if (showUnavailableItemsModal) {
    return (
      <RemoveUnavailableItemsModal
        onConfirmCb={handleUnavailableModalDismiss}
        unavailableCartEntries={unavailableCartEntries}
      />
    );
  }

  return (
    <>
      <CartContent
        hasAddOns={addOnSections.length > 0}
        checkoutPaymentMethod={checkoutPaymentMethod}
      />
      {cardSuccessfullyAdded && (
        <VisuallyHidden accessibilityLabel={t('yourNewPaymentMethodHasBeenSuccessfullySaved')} />
      )}
    </>
  );
};
